import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Phone, Mail } from 'lucide-react';

const LinkedinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const Contact: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="contact" style={styles.section} ref={ref}>
      <div style={styles.container}>
        {/* Left column - info */}
        <motion.div
          style={styles.info}
          initial={{ opacity: 0, x: -40 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <span style={styles.label}>Contacto</span>
          <h2 style={styles.title}>
            Vamos trabalhar<br />
            <span style={styles.titleAccent}>juntos</span>
          </h2>
          <p style={styles.desc}>
            Está pronto para dar o próximo passo na transformação digital do seu negócio?
            Entre em contacto com a nossa equipa.
          </p>

          <div style={styles.contacts}>
            <a href="mailto:hello@2gfinnovationsystems.pt" style={styles.contactRow}>
              <div style={styles.contactIcon}><Mail size={18} color="#0057FF" /></div>
              <div>
                <span style={styles.contactLabel}>Email</span>
                <span style={styles.contactVal}>hello@2gfinnovationsystems.pt</span>
              </div>
            </a>

            <a href="tel:+351969059898" style={styles.contactRow}>
              <div style={styles.contactIcon}><Phone size={18} color="#0057FF" /></div>
              <div>
                <span style={styles.contactLabel}>Telefone</span>
                <span style={styles.contactVal}>+351 969 059 898</span>
              </div>
            </a>

            <div style={styles.contactRow}>
              <div style={styles.contactIcon}><MapPin size={18} color="#0057FF" /></div>
              <div>
                <span style={styles.contactLabel}>Morada</span>
                <span style={styles.contactVal}>Centro de Inovação Empresarial da Covilhã<br />
                R. António Augusto de Aguiar, 6200-053 Covilhã, Portugal</span>
              </div>
            </div>
          </div>

          <a
            href="https://linkedin.com/company/2gf-innovation-systems"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.linkedinBtn}
          >
            <LinkedinIcon />
            LinkedIn
          </a>
        </motion.div>

        {/* Right column - Vulpes contact widget */}
        <motion.div
          style={styles.formWrap}
          initial={{ opacity: 0, x: 40 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div data-vulpes-contacto />
        </motion.div>
      </div>
    </section>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  section: {
    background: '#F3F6FB',
    padding: '120px 0',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'grid',
    gridTemplateColumns: '1fr 1.3fr',
    gap: '5rem',
    alignItems: 'start',
  },
  info: {},
  label: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: '#00C896',
    marginBottom: '1rem',
    display: 'block',
  },
  title: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    fontWeight: 900,
    color: '#1A2535',
    lineHeight: 1.1,
    marginBottom: '1.25rem',
    letterSpacing: '-0.02em',
  },
  titleAccent: {
    color: '#0057FF',
  },
  desc: {
    fontSize: '0.95rem',
    color: '#516273',
    lineHeight: 1.8,
    marginBottom: '2.5rem',
  },
  contacts: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    marginBottom: '2rem',
  },
  contactRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    textDecoration: 'none',
  },
  contactIcon: {
    width: '44px',
    height: '44px',
    background: 'rgba(0,87,255,0.08)',
    border: '1px solid rgba(0,87,255,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  contactLabel: {
    display: 'block',
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#8A99AF',
    marginBottom: '0.2rem',
  },
  contactVal: {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#1A2535',
    lineHeight: 1.5,
  },
  linkedinBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.6rem',
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#0057FF',
    background: 'rgba(0,87,255,0.06)',
    border: '1px solid rgba(0,87,255,0.2)',
    padding: '0.75rem 1.5rem',
    textDecoration: 'none',
  },
  formWrap: {
    background: '#ffffff',
    padding: '3rem',
    boxShadow: '0 4px 40px rgba(0,0,0,0.06)',
  },
};

export default Contact;
